/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Image from 'gatsby-image'
import Swiper from 'react-id-swiper'
import { Arrow } from '../shared/icons'
import { LinkType } from '../shared'

import {
  desktopVw,
  mobileVw,
  mobileBreakpoint,
  desktopBreakpoint,
  colors,
  letterSpacing,
  fonts,
} from '../../styles'

import 'swiper/css/swiper.css'
import { useLang } from '../../hooks'

export const CollectionMenu = ({ data, menu, refContainer }) => {
  const [swiper, setSwiper] = useState(null)
  const lang = useLang();
  const params = {
    pagination: {
      el: '.swiper-pagination',
      type: 'progressbar',
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    watchOverflow: true,
    breakpoints: {
      320: {
        slidesPerView: 1,
      },
      1366: {
        slidesPerView: 4,
      },
    },
  }

  return (
    <Wrapper>
      {data.map(({ slides, title }, i) => (
        <SwiperWrapper
          key={i}
          ref={i === menu ? refContainer : null}
          showProgressBar={slides.length > 4}
        >
          <Title>{title}</Title>
          <Swiper {...params} getSwiper={setSwiper}>
            {slides.map((slide, i) => (
              <LinkType
                key={i}
                to={`/${lang}${slide.slug}`}
                alt={`Ce lien va ouvrir une nouvelle fenêtre: ${slide.title}`}
              >
                <SlideWrapper>
                  <StyledImage
                    fluid={slide.thumbnailImage.fluid}
                    alt={slide.thumbnailImage.title}
                    imgStyle={{ opacity: '1' }}
                  />
                  <Subtitle>{slide.tags ? slide.tags : slide.title}</Subtitle>
                </SlideWrapper>
              </LinkType>
            ))}
          </Swiper>
        </SwiperWrapper>
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: ${mobileVw(25)} 0 0;
  display: flex;
  flex-direction: column;
  position: relative;

  .swiper-container {
    margin-left: 0;
    width: 100%;
  }

  .swiper-slide {
    height: auto;
    padding-left: ${mobileVw(16)};
  }

  @media (min-width: ${desktopBreakpoint}) {
    margin: ${desktopVw(47)} 0 0;

    .swiper-container {
      padding: 0 ${desktopVw(128)} 0 ${desktopVw(98)};
      overflow: visible;
    }

    .swiper-slide {
      padding-left: ${desktopVw(30)};
      cursor: pointer;
    }
  }
`

const SwiperWrapper = styled.div`
  .swiper-pagination-progressbar,
  .swiper-button-prev,
  .swiper-button-next {
    display: none;
  }

  @media (min-width: ${desktopBreakpoint}) {
    margin-bottom: ${desktopVw(75)};
    position: relative;

    .swiper-pagination-progressbar,
    .swiper-pagination {
      display: ${(props) => (props.showProgressBar ? 'inline-block' : 'none')};
      position: absolute;
      top: ${desktopVw(-35)};
      left: unset;
      right: ${desktopVw(128)};
      background: ${colors.darkGrey};
      width: ${desktopVw(253)};
      height: ${desktopVw(2)};
    }

    .swiper-pagination-progressbar-fill {
      background: ${colors.mailleGold};
      position: absolute;
    }

    .swiper-container {
      .swiper-button-prev {
        transform: rotate(180deg);
        left: 0;
        background-color: ${colors.lightGrey};
      }

      .swiper-button-next {
        right: 0;
        background-color: ${colors.mailleGold};
      }

      .swiper-button-prev,
      .swiper-button-next {
        display: ${(props) => (props.showProgressBar ? 'flex' : 'none')};
        width: ${desktopVw(64)};
        height: ${desktopVw(64)};

        &:after {
          content: '';
          height: 15px;
          width: 15px;
        }
      }

      .swiper-button-prev {
        &:after {
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAxNiAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwKSI+CjxwYXRoIGQ9Ik0xMy43OTQ5IDcuNTI5OTZIMFY2LjUyOTk2SDEzLjc5MjhMNy45NzA0MyAwLjcwNzU0OUw4LjY3NzU0IDAuMDAwNDQyNTA1TDE1LjcwODEgNy4wMzFMOC42Nzc1NCAxNC4wNjE2TDcuOTcwNDMgMTMuMzU0NEwxMy43OTQ5IDcuNTI5OTZaIiBmaWxsPSJibGFjayIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwIj4KPHJlY3Qgd2lkdGg9IjE1LjcwNyIgaGVpZ2h0PSIxNC4wNiIgZmlsbD0id2hpdGUiLz4KPC9jbGlwUGF0aD4KPC9kZWZzPgo8L3N2Zz4K');
        }
      }

      .swiper-button-next {
        &:after {
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAxNiAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwKSI+CjxwYXRoIGQ9Ik0xMy43OTQ5IDcuNTI5OTZIMFY2LjUyOTk2SDEzLjc5MjhMNy45NzA0MyAwLjcwNzU0OUw4LjY3NzU0IDAuMDAwNDQyNTA1TDE1LjcwODEgNy4wMzFMOC42Nzc1NCAxNC4wNjE2TDcuOTcwNDMgMTMuMzU0NEwxMy43OTQ5IDcuNTI5OTZaIiBmaWxsPSJ3aGl0ZSIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwIj4KPHJlY3Qgd2lkdGg9IjE1LjcwNyIgaGVpZ2h0PSIxNC4wNiIgZmlsbD0id2hpdGUiLz4KPC9jbGlwUGF0aD4KPC9kZWZzPgo8L3N2Zz4K');
        }
      }
    }
  }
`

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const StyledImage = styled(Image)`
  width: 100%;
  height: ${mobileVw(264)};

  @media (min-width: ${desktopBreakpoint}) {
    max-height: ${desktopVw(384)};
  }
`

const Title = styled.h2`
  font-family: ${fonts.splandor};
  color: ${colors.mailleGold};
  font-size: ${mobileVw(30)};
  margin-bottom: ${mobileVw(8)};
  padding-left: ${mobileVw(16)};

  @media (min-width: ${desktopBreakpoint}) {
    font-size: ${desktopVw(34)};
    margin-bottom: ${desktopVw(15)};
    padding-left: ${desktopVw(128)};
    display: inline-block;
  }
`

const Subtitle = styled.span`
  font-family: ${fonts.splandor};
  font-size: ${mobileVw(18)};
  padding: ${mobileVw(18)} 0 ${mobileVw(33)};

  @media (min-width: ${desktopBreakpoint}) {
    font-size: ${desktopVw(18)};
    font-family: ${fonts.tivoli};
    text-transform: uppercase;
    letter-spacing: ${desktopVw(1.8)};
    padding: ${desktopVw(16)} 0 ${desktopVw(12)};
    border: solid ${desktopVw(1)} ${colors.mailleGold};
    margin: ${desktopVw(16)} 0 0;
    text-align: center;
    color: ${colors.mailleGold};
  }
`
